"use client";

import { useMemo } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  decimalText,
  pointValueText,
  shown,
} from "@/components/tools/calculus/format";
import { FunctionGraph } from "@/components/tools/calculus/function-graph";
import { MathView } from "@/components/tools/calculus/math-view";
import { readConstant } from "@/components/tools/calculus/read-constant";
import {
  PLANE_HEIGHT,
  PLANE_WIDTH,
} from "@/components/tools/math/coordinate-plane";
import type {
  TANGENT_LINE_CALCULATOR_STRINGS,
  TangentView,
} from "@/components/tools/tangent-line-calculator";
import type { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { compile } from "@/lib/tool-kit/calculus/evaluate";
import { autoWindow, type Window } from "@/lib/tool-kit/calculus/plot";
import { toLatex, toText } from "@/lib/tool-kit/calculus/print";
import {
  type Line,
  standardText,
  type TangentResult,
} from "@/lib/tool-kit/calculus/tangent";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Strings = typeof TANGENT_LINE_CALCULATOR_STRINGS;
type Fmt = ReturnType<typeof useToolNumber>;
type OkView = Extract<TangentView, { kind: "ok" }>;

function LineBlock({
  heading,
  line,
  decimals,
  copyLabel,
  strings,
  fmt,
}: {
  heading: string;
  line: Line;
  decimals: boolean;
  copyLabel: string;
  strings: Strings;
  fmt: Fmt;
}) {
  const options = { decimals };
  const vertical = line.slopeIntercept === null;
  const slopeIntercept = line.slopeIntercept;
  let copyText = "";
  if (slopeIntercept) {
    copyText = `y = ${toText(slopeIntercept, options)}`;
  } else if (line.standard) {
    copyText = standardText(line.standard);
  }
  const decimalLine =
    line.standard === null && !vertical
      ? `${decimalText(fmt, line.slopeValue)}x ${line.interceptValue < 0 ? "−" : "+"} ${decimalText(fmt, Math.abs(line.interceptValue))}`
      : null;
  return (
    <div className={`${RESULT_CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{heading}</h3>
      <div className="space-y-3 text-sm">
        {line.pointSlope ? (
          <div>
            <p className="text-muted-foreground">{strings.pointSlopeForm}</p>
            <div className="text-foreground">
              <MathView
                label={`${shown(line.pointSlope.left, decimals)} = ${shown(line.pointSlope.right, decimals)}`}
                latex={`${toLatex(line.pointSlope.left, options)} = ${toLatex(line.pointSlope.right, options)}`}
              />
            </div>
          </div>
        ) : null}
        {slopeIntercept ? (
          <div>
            <p className="text-muted-foreground">
              {strings.slopeInterceptForm}
            </p>
            <div className="text-foreground">
              <MathView
                label={`y = ${shown(slopeIntercept, decimals)}`}
                latex={`y = ${toLatex(slopeIntercept, options)}`}
              />
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground">{strings.verticalLineNote}</p>
        )}
        {line.standard ? (
          <div>
            <p className="text-muted-foreground">{strings.standardForm}</p>
            <div className="font-mono text-foreground">
              {standardText(line.standard)}
            </div>
          </div>
        ) : null}
        {decimalLine ? (
          <div>
            <p className="text-muted-foreground">{strings.decimalForm}</p>
            <div className="font-mono text-foreground">
              {t(strings.decimalLine, { line: decimalLine })}
            </div>
          </div>
        ) : null}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={copyLabel}
        small
        text={copyText}
      />
    </div>
  );
}

function Approximation({
  view,
  estimateText,
  strings,
  fmt,
}: {
  view: OkView;
  estimateText: string;
  strings: Strings;
  fmt: Fmt;
}) {
  const result = view.result as Extract<TangentResult, { kind: "ok" }>;
  const x1 = readConstant(estimateText);
  if (x1 === null) {
    return null;
  }
  if (x1 === "invalid") {
    return <ErrorNotice>{strings.pointInvalid}</ErrorNotice>;
  }
  const f = compile(view.ast, "x");
  const actual = f(x1.value);
  const linear =
    result.point.value + result.slope.value * (x1.value - view.point.value);
  const error = actual - linear;
  const relative = actual === 0 ? Number.NaN : Math.abs(error / actual);
  const pointText = shown(view.point.ast, view.decimals);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.approxHeading}</h3>
      <p className="text-muted-foreground text-sm">
        {t(strings.approxIntro, { point: pointText })}
      </p>
      <StatGrid>
        <StatCard
          label={strings.approxLinear}
          value={decimalText(fmt, linear)}
        />
        <StatCard
          label={strings.approxActual}
          value={
            Number.isFinite(actual)
              ? decimalText(fmt, actual)
              : strings.undefinedHere
          }
        />
        <StatCard
          label={strings.approxError}
          value={
            Number.isFinite(error)
              ? decimalText(fmt, error)
              : strings.undefinedHere
          }
        />
        <StatCard
          label={strings.approxRelative}
          value={
            Number.isFinite(relative)
              ? fmt(relative, { style: "percent", maximumSignificantDigits: 3 })
              : strings.undefinedHere
          }
        />
      </StatGrid>
      {Number.isFinite(relative) && relative > 0.1 ? (
        <Notice>{strings.approxWarning}</Notice>
      ) : null}
    </div>
  );
}

function graphWindow(
  view: OkView,
  halfWidth: number,
  equalScale: boolean,
  f: (x: number) => number,
  yAt: number
): Window {
  const a = view.point.value;
  const xMin = a - halfWidth;
  const xMax = a + halfWidth;
  if (equalScale) {
    const halfHeight = (halfWidth * PLANE_HEIGHT) / PLANE_WIDTH;
    return { xMin, xMax, yMin: yAt - halfHeight, yMax: yAt + halfHeight };
  }
  return autoWindow([f], xMin, xMax);
}

function Graph({
  view,
  result,
  halfWidth,
  equalScale,
  strings,
  fmt,
}: {
  view: OkView;
  result: Extract<TangentResult, { kind: "ok" }>;
  halfWidth: number;
  equalScale: boolean;
  strings: Strings;
  fmt: Fmt;
}) {
  const f = useMemo(() => compile(view.ast, "x"), [view.ast]);
  const a = view.point.value;
  const m = result.slope.value;
  const y0 = result.point.value;
  const verticalNormal = result.normal.slope === null;
  const normalSlope = result.normal.slopeValue;
  const window = graphWindow(view, halfWidth, equalScale, f, y0);
  const pointLabel = t(strings.pointLabelText, {
    x: fmt(a, { maximumFractionDigits: 3 }),
    y: fmt(y0, { maximumFractionDigits: 3 }),
  });
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
      <FunctionGraph
        downloadPngLabel={strings.downloadPng}
        downloadSvgLabel={strings.downloadSvg}
        filename="tangent-line-graph"
        over={
          verticalNormal
            ? (scales) => (
                <line
                  stroke={CHART_COLORS[3]}
                  strokeDasharray="6 4"
                  strokeWidth={2}
                  x1={scales.x(a)}
                  x2={scales.x(a)}
                  y1={-1e4}
                  y2={1e4}
                />
              )
            : undefined
        }
        points={[
          {
            x: a,
            y: y0,
            color: CHART_COLORS[7],
            label: pointLabel,
            below: true,
          },
        ]}
        series={[
          { f, color: CHART_COLORS[0], label: strings.seriesFunction },
          {
            f: (x) => y0 + m * (x - a),
            color: CHART_COLORS[2],
            dashed: true,
            label: strings.seriesTangent,
          },
          ...(verticalNormal
            ? []
            : [
                {
                  f: (x: number) => y0 + normalSlope * (x - a),
                  color: CHART_COLORS[3],
                  dashed: true,
                  label: strings.seriesNormal,
                },
              ]),
        ]}
        title={t(strings.graphTitle, {
          point: shown(view.point.ast, view.decimals),
        })}
        window={window}
      />
    </div>
  );
}

function failureText(
  result: Exclude<TangentResult, { kind: "ok" }>,
  point: string,
  strings: Strings,
  fmt: Fmt
): string {
  if (result.kind === "undefined") {
    return t(strings.failureUndefined, { point });
  }
  const values = {
    point,
    left: decimalText(fmt, result.left.value),
    right: decimalText(fmt, result.right.value),
  };
  switch (result.reason) {
    case "corner":
      return t(strings.failureCorner, values);
    case "vertical":
      return t(strings.failureVertical, values);
    case "cusp":
      return t(strings.failureCusp, values);
    default:
      return t(strings.failureUnbounded, values);
  }
}

export function TangentResults({
  view,
  estimateText,
  halfWidth,
  equalScale,
  strings,
  fmt,
}: {
  view: OkView;
  estimateText: string;
  halfWidth: number;
  equalScale: boolean;
  strings: Strings;
  fmt: Fmt;
}) {
  const { result, decimals } = view;
  const pointText = shown(view.point.ast, decimals);
  if (result.kind !== "ok") {
    return (
      <ErrorNotice>{failureText(result, pointText, strings, fmt)}</ErrorNotice>
    );
  }
  const normalSlope = result.normal.slope;
  return (
    <>
      <StatGrid>
        <StatCard
          label={strings.pointValue}
          value={`(${pointText}, ${pointValueText(result.point, fmt, strings.undefinedHere, decimals)})`}
        />
        <StatCard
          emphasis
          label={strings.slopeValue}
          value={pointValueText(
            result.slope,
            fmt,
            strings.undefinedHere,
            decimals
          )}
        />
        <StatCard
          label={strings.normalSlopeValue}
          value={
            normalSlope ? shown(normalSlope, decimals) : strings.verticalNormal
          }
        />
      </StatGrid>
      <LineBlock
        copyLabel={strings.copyTangent}
        decimals={decimals}
        fmt={fmt}
        heading={strings.tangentHeading}
        line={result.tangent}
        strings={strings}
      />
      <LineBlock
        copyLabel={strings.copyNormal}
        decimals={decimals}
        fmt={fmt}
        heading={strings.normalHeading}
        line={result.normal}
        strings={strings}
      />
      <Approximation
        estimateText={estimateText}
        fmt={fmt}
        strings={strings}
        view={view}
      />
      <Graph
        equalScale={equalScale}
        fmt={fmt}
        halfWidth={halfWidth}
        result={result}
        strings={strings}
        view={view}
      />
    </>
  );
}
